// img-flash — a stack of images cycling one at a time, each new one wiping in over the
// last with a clip-path reveal. Autoplays only while the section is in view.
import { onDestroy, onView } from "@/modules/_";
import gsap, { reduced } from "@lib/gsap";

const HOLD_MS = 1600; // dwell on a fully-revealed image before the next wipe starts
const REVEAL_DURATION = 0.8; // clip-path wipe length
const REVEAL_EASE = "power3.inOut";
// Incoming image settles from a slight zoom as it wipes in — adds depth to the cut. The
// overscan also gives the parallax push (dx/dy below) room to slide without baring an edge.
const IMG_FROM_SCALE = 1.12;
const IMG_TO_SCALE = 1;
// How far the image slides under the mask, as % of its size. Keep ≲ the scale overscan
// (~6% each side at 1.12) so the trailing edge never shows through the revealed area.
const PUSH = 6;

// hidden → shown clip-path pairs; one is picked per reveal (never the same twice running).
// `dx`/`dy` push the image a few % in the wipe's direction as it reveals — the signature
// "motion under a mask" parallax, not a static photo being uncovered.
// Every value carries a unit — GSAP snaps (no tween) if it has to interpolate `100%` against
// a unitless `0`, which reads as a plain hide/show. Polygons keep a fixed point count so
// each vertex interpolates cleanly.
type Wipe = { from: string; to: string; dx?: number; dy?: number };
const WIPES: Wipe[] = [
  // edge wipes — image trails in from the side the reveal comes from
  { from: "inset(0% 100% 0% 0%)", to: "inset(0% 0% 0% 0%)", dx: -PUSH }, // left → right
  { from: "inset(0% 0% 0% 100%)", to: "inset(0% 0% 0% 0%)", dx: PUSH }, // right → left
  { from: "inset(0% 0% 100% 0%)", to: "inset(0% 0% 0% 0%)", dy: -PUSH }, // top → bottom
  { from: "inset(100% 0% 0% 0%)", to: "inset(0% 0% 0% 0%)", dy: PUSH }, // bottom → top
  // barn-door splits — open from a center seam outward
  { from: "inset(0% 50% 0% 50%)", to: "inset(0% 0% 0% 0%)" }, // vertical seam → wide
  { from: "inset(50% 0% 50% 0%)", to: "inset(0% 0% 0% 0%)" }, // horizontal seam → tall
  // diagonal parallelogram sweep — a slanted leading edge crossing the frame
  {
    from: "polygon(0% 0%, 0% 0%, -40% 100%, -40% 100%)",
    to: "polygon(0% 0%, 140% 0%, 100% 100%, 0% 100%)",
    dx: -PUSH,
  },
  // iris — circle opening from center
  { from: "circle(0% at 50% 50%)", to: "circle(150% at 50% 50%)" },
];

export default function (element: HTMLElement, _dataset: DOMStringMap) {
  const items = Array.from(
    element.querySelectorAll<HTMLElement>('[img-flash="item"]'),
  );
  if (items.length < 2) return; // nothing to cycle

  const prevPosition = element.style.position;
  if (getComputedStyle(element).position === "static") {
    element.style.position = "relative";
  }

  // Item 0 stays in flow so the container keeps its intrinsic height; the rest overlay it
  // absolutely. (Assumes the items share a size — true for cover images in one box.)
  const prevItem = items.map((it) => ({
    position: it.style.position,
    inset: it.style.inset,
    width: it.style.width,
    height: it.style.height,
    zIndex: it.style.zIndex,
    clipPath: it.style.clipPath,
    willChange: it.style.willChange,
  }));
  const imgs = items.map((it) => it.querySelector<HTMLElement>("img"));
  const prevImgTransform = imgs.map((img) => img?.style.transform ?? "");

  items.forEach((it, i) => {
    if (i === 0) {
      it.style.position = "relative";
    } else {
      it.style.position = "absolute";
      it.style.inset = "0";
      it.style.width = "100%";
      it.style.height = "100%";
    }
    it.style.zIndex = i === 0 ? "1" : "0";
  });

  let current = 0;
  let lastWipe = -1;
  let running = false;
  let timer: number | null = null;

  function clearTimer() {
    if (timer !== null) {
      window.clearTimeout(timer);
      timer = null;
    }
  }

  function pickWipe() {
    // Uniform over every wipe except the last one used — pick from the other n-1 slots and
    // skip past lastWipe, so no direction is favoured (a naive `+1 on collision` biases one).
    let i = Math.floor(Math.random() * (WIPES.length - (lastWipe < 0 ? 0 : 1)));
    if (lastWipe >= 0 && i >= lastWipe) i++;
    lastWipe = i;
    return WIPES[i];
  }

  function reveal(next: number) {
    const incoming = items[next];
    const img = imgs[next];

    // Newest on top, the outgoing one directly beneath it, everyone else hidden below —
    // so the wipe shows the incoming image over the one it's replacing.
    items.forEach((it) => (it.style.zIndex = "0"));
    items[current].style.zIndex = "1";
    incoming.style.zIndex = "2";

    const finish = () => {
      incoming.style.willChange = "";
      if (img) img.style.willChange = "";
      current = next;
      if (running) schedule();
    };

    if (reduced) {
      incoming.style.clipPath = "";
      if (img) img.style.transform = "";
      finish();
      return;
    }

    const wipe = pickWipe();
    incoming.style.willChange = "clip-path";
    gsap.fromTo(
      incoming,
      { clipPath: wipe.from },
      {
        clipPath: wipe.to,
        duration: REVEAL_DURATION,
        ease: REVEAL_EASE,
        onComplete: () => {
          // Fully shown now — drop the clip so it can't trim a later reflow.
          incoming.style.clipPath = "";
          finish();
        },
      },
    );
    if (img) {
      img.style.willChange = "transform";
      gsap.fromTo(
        img,
        {
          scale: IMG_FROM_SCALE,
          xPercent: wipe.dx ?? 0,
          yPercent: wipe.dy ?? 0,
        },
        {
          scale: IMG_TO_SCALE,
          xPercent: 0,
          yPercent: 0,
          duration: REVEAL_DURATION,
          ease: REVEAL_EASE,
        },
      );
    }
  }

  function pickNext() {
    // Uniform over every image except the one showing — same trick as pickWipe.
    let i = Math.floor(Math.random() * (items.length - 1));
    if (i >= current) i++;
    return i;
  }

  function schedule() {
    clearTimer();
    timer = window.setTimeout(() => {
      timer = null;
      reveal(pickNext());
    }, HOLD_MS);
  }

  // Pause the cycle off-screen; resume when it scrolls back in.
  onView(element, {
    autoStart: true,
    callback: ({ isIn }) => {
      if (isIn && !running) {
        running = true;
        schedule();
      } else if (!isIn && running) {
        running = false;
        clearTimer();
      }
    },
  });

  onDestroy(() => {
    running = false;
    clearTimer();
    items.forEach((it, i) => {
      gsap.killTweensOf(it);
      const img = imgs[i];
      if (img) gsap.killTweensOf(img);
    });
    items.forEach((it, i) => {
      it.style.position = prevItem[i].position;
      it.style.inset = prevItem[i].inset;
      it.style.width = prevItem[i].width;
      it.style.height = prevItem[i].height;
      it.style.zIndex = prevItem[i].zIndex;
      it.style.clipPath = prevItem[i].clipPath;
      it.style.willChange = prevItem[i].willChange;
      const img = imgs[i];
      if (img) img.style.transform = prevImgTransform[i];
    });
    element.style.position = prevPosition;
  });
}
